import { useState } from 'react'
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '../auth'
import { INDEX_APP_CONNECTION } from '../user/permission'
import {
    completeAppConnection,
    configureTools,
    connectApp,
    disconnectApp,
    fetchAppCatalog,
    fetchAppConnections,
    fetchAppTools,
} from './api'

type CompleteAppInput = {
    id: string
    state: string
}

// Poll while a sign-in runs in another tab, so the app appears without a reload.
export function useAppConnections({ poll = false }: { poll?: boolean } = {}) {
    const { activeCompany, can } = useAuth()

    return useQuery({
        queryKey: ['app-connections', activeCompany?.id],
        queryFn: fetchAppConnections,
        enabled: !!activeCompany && can(INDEX_APP_CONNECTION),
        refetchOnWindowFocus: 'always',
        refetchInterval: poll ? 3000 : false,
    })
}

export function useAppCatalog(search: string) {
    const { activeCompany } = useAuth()

    return useInfiniteQuery({
        queryKey: ['app-catalog', activeCompany?.id, search],
        initialPageParam: null as string | null,
        queryFn: ({ pageParam }) => fetchAppCatalog(search, pageParam),
        getNextPageParam: (page) => page.nextCursor ?? undefined,
    })
}

export function useAppTools(connectionId: string | undefined, search: string) {
    const { activeCompany } = useAuth()

    return useInfiniteQuery({
        queryKey: ['app-tools', activeCompany?.id, connectionId, search],
        initialPageParam: null as string | null,
        enabled: !!connectionId,
        queryFn: ({ pageParam }) => fetchAppTools(connectionId!, search, pageParam),
        getNextPageParam: (page) => page.nextCursor ?? undefined,
    })
}

export function useConnectApp() {
    const client = useQueryClient()

    return useMutation({
        mutationFn: connectApp,
        onSuccess: () => client.invalidateQueries({ queryKey: ['app-connections'] }),
    })
}

export function useDisconnectApp() {
    const client = useQueryClient()

    return useMutation({
        mutationFn: disconnectApp,
        onSettled: () => client.invalidateQueries({ queryKey: ['app-connections'] }),
    })
}

export function useCompleteApp() {
    const client = useQueryClient()

    return useMutation({
        mutationFn: ({ id, state }: CompleteAppInput) => completeAppConnection(id, state),
        onSuccess: () => client.invalidateQueries({ queryKey: ['app-connections'] }),
    })
}

export function useConfigureTools() {
    return useMutation({
        mutationFn: configureTools,
    })
}

// Open the sign-in tab before the request finishes, so browsers do not block it as a popup.
export function useOpenConnection() {
    const connect = useConnectApp()
    const [redirectUrl, setRedirectUrl] = useState<string>()

    const open = (toolkit: string) => {
        const popup = window.open('about:blank', '_blank')

        if (popup) {
            popup.opener = null
        }

        return connect
            .mutateAsync(toolkit)
            .then((result) => {
                setRedirectUrl(result.redirectUrl)

                if (popup) {
                    popup.location.href = result.redirectUrl
                }

                return result
            })
            .catch((error) => {
                popup?.close()

                throw error
            })
    }

    return {
        open,
        redirectUrl,
        isPending: connect.isPending,
        isError: connect.isError,
    }
}
